import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { styleSelect, themeSelect } from '../../../../rbts/assets/ts/_utils';
import { obtenerEventosConTicketsSelect, obtenerEventosSelect } from '../../../service/eventos/Eventos';
import {
  obtenerTicketsPorEventoService,
  verificarTicketsExistenService,
  insertarFisicosService,
} from '../../../service/tickets/Tickets';
import { TicketCard } from './TicketCard';
import { TicketsPdf } from './TicketsPdf';

type EventoOption = {
  id: number;
  value: string;
  label: string;
};

type ResultadoInsert = {
  insertados: number;
  yaExistian: string[];
};

const InsertPanel: React.FC<{ eventoActivo: string | null; eventoActivoId: number | null; onInsertSuccess: () => void }> = ({ eventoActivo, eventoActivoId, onInsertSuccess }) => {
  const [folioInsertDesde, setFolioInsertDesde] = useState<string>('');
  const [folioInsertHasta, setFolioInsertHasta] = useState<string>('');
  const [insertando, setInsertando] = useState<boolean>(false);
  const [resultadoInsert, setResultadoInsert] = useState<ResultadoInsert | null>(null);
  const [errorInsert, setErrorInsert] = useState<string | null>(null);

  const handleInsertarFisicos = async () => {
    if (!folioInsertDesde || !folioInsertHasta) return;
    setInsertando(true);
    setResultadoInsert(null);
    setErrorInsert(null);
    try {
      const resultado = await insertarFisicosService(folioInsertDesde, folioInsertHasta, eventoActivoId);
      setResultadoInsert(resultado);
      if (resultado.insertados > 0) {
        onInsertSuccess();
      }
    } catch {
      setErrorInsert('Ocurrió un error al insertar los tickets. Intenta de nuevo.');
    } finally {
      setInsertando(false);
    }
  };

  return (
    <div className="card h-100">
      <div className="card-header border-0 pt-6">
        <div className="card-title">
          <h3 className="card-label fw-bolder text-dark">Insertar en BD</h3>
        </div>
      </div>
      <div className="card-body py-3">
        {eventoActivo && (
          <div className="mb-4">
            <label className="form-label fw-bold">Evento</label>
            <div className="form-control bg-light text-muted">{eventoActivo}</div>
          </div>
        )}
        <div className="mb-4">
          <label className="form-label fw-bold">Folio desde</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={7}
            className="form-control"
            placeholder="Ej: 20000"
            value={folioInsertDesde}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              setFolioInsertDesde(val);
              setResultadoInsert(null);
              setErrorInsert(null);
            }}
          />
        </div>
        <div className="mb-4">
          <label className="form-label fw-bold">Folio hasta</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={7}
            className="form-control"
            placeholder="Ej: 20500"
            value={folioInsertHasta}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              setFolioInsertHasta(val);
              setResultadoInsert(null);
              setErrorInsert(null);
            }}
          />
        </div>
        <button
          className="btn btn-primary fw-bold w-100"
          onClick={handleInsertarFisicos}
          disabled={!folioInsertDesde || !folioInsertHasta || insertando}
        >
          {insertando ? (
            <>
              Insertando...
              <span className="spinner-border spinner-border-sm ms-2"></span>
            </>
          ) : (
            <>
              <i className="bi bi-database-add me-2"></i>
              Insertar en BD
            </>
          )}
        </button>

        {errorInsert && (
          <div className="alert alert-danger mt-4">{errorInsert}</div>
        )}

        {resultadoInsert && (
          <div className={`alert mt-4 ${resultadoInsert.insertados > 0 ? 'alert-success' : 'alert-warning'}`}>
            {resultadoInsert.insertados > 0 && (
              <div>
                <strong>✅ Se insertaron {resultadoInsert.insertados} ticket(s)</strong>
                {` (folios ${folioInsertDesde} al ${folioInsertHasta})`}
              </div>
            )}
            {resultadoInsert.yaExistian.length > 0 && (
              <div className={resultadoInsert.insertados > 0 ? 'mt-2' : ''}>
                <strong>⚠️ Folios que ya existían (no insertados):</strong>{' '}
                {resultadoInsert.yaExistian.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const View: React.FC = () => {
  const [eventoOptions, setEventoOptions] = useState<EventoOption[]>([]);
  const [eventoSeleccionado, setEventoSeleccionado] = useState<EventoOption | null>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loadingEventos, setLoadingEventos] = useState<boolean>(false);
  const [loadingTickets, setLoadingTickets] = useState<boolean>(false);
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [folioMin, setFolioMin] = useState<string>('');
  const [folioMax, setFolioMax] = useState<string>('');
  const [checkingExisten, setCheckingExisten] = useState<boolean>(true);
  const [ticketsExisten, setTicketsExisten] = useState<boolean>(false);
  const [eventoActivo, setEventoActivo] = useState<string | null>(null);
  const [eventoActivoId, setEventoActivoId] = useState<number | null>(null);

  useEffect(() => {
    const init = async () => {
      const [existen, eventos] = await Promise.all([
        verificarTicketsExistenService(),
        obtenerEventosSelect().catch(() => []),
      ]);
      setTicketsExisten(existen);
      const activo = eventos.find((e: any) => e.actual);
      if (activo) {
        setEventoActivo(activo.label);
        setEventoActivoId(activo.id);
      }
      setCheckingExisten(false);
    };
    init();
  }, []);

  const cargarEventos = async () => {
    if (eventoOptions.length > 0) return;
    setLoadingEventos(true);
    try {
      const data = await obtenerEventosConTicketsSelect();
      setEventoOptions(data);
    } catch {
      // silencioso
    } finally {
      setLoadingEventos(false);
    }
  };

  const handleSelectEvento = async (option: EventoOption | null) => {
    setEventoSeleccionado(option);
    setTickets([]);
    setShowPdf(false);
    if (!option) return;
    setLoadingTickets(true);
    try {
      const data = await obtenerTicketsPorEventoService(option.id);
      setTickets(Array.isArray(data) ? data : []);
    } catch {
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const ticketsFiltrados = tickets.filter((ticket: any) => {
    const folio = parseInt(ticket.folio, 10);
    const min = folioMin !== '' ? parseInt(folioMin, 10) : -Infinity;
    const max = folioMax !== '' ? parseInt(folioMax, 10) : Infinity;
    return folio >= min && folio <= max;
  });

  const rangoLleno = folioMin !== '' && folioMax !== '';

  if (checkingExisten) {
    return (
      <div className="card">
        <div className="card-body py-10 d-flex justify-content-center">
          <span className="indicator-progress" style={{ display: 'block' }}>
            Verificando tickets...
            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
          </span>
        </div>
      </div>
    );
  }

  /* ── Sin tickets: mostrar solo el panel de inserción ─────────────────── */
  if (!ticketsExisten) {
    return (
      <div className="card">
        <div className="card-header border-0 pt-6">
          <div className="card-title">
            <h3 className="card-label fw-bolder text-dark">Generación de Tickets con QR</h3>
          </div>
        </div>
        <div className="card-body py-3">
          <p className="text-muted mb-4">
            No hay tickets físicos en la base de datos. Define el rango de folios e insértalos.
          </p>
          <InsertPanel eventoActivo={eventoActivo} eventoActivoId={eventoActivoId} onInsertSuccess={() => setTicketsExisten(true)} />
        </div>
      </div>
    );
  }

  /* ── Con tickets: vista normal + panel de inserción a la derecha ──────── */
  return (
    <div className="row g-6 align-items-start">
      {/* ── Columna principal ───────────────────────────────────────────── */}
      <div className="col-lg-8">
        <div className="card">
          <div className="card-header border-0 pt-6">
            <div className="card-title">
              <h3 className="card-label fw-bolder text-dark">Generación de Tickets con QR</h3>
            </div>
          </div>
          <div className="card-body py-3">
            <div className="row mb-6 align-items-end">
              <div className="col-md-5">
                <label className="form-label fw-bold">Seleccionar Evento</label>
                <Select
                  placeholder={
                    rangoLleno ? 'Selecciona un evento...' : 'Ingresa el rango de folios primero'
                  }
                  options={eventoOptions}
                  value={eventoSeleccionado}
                  onChange={(opt) => handleSelectEvento(opt as EventoOption | null)}
                  onMenuOpen={cargarEventos}
                  isLoading={loadingEventos}
                  isClearable
                  isDisabled={!rangoLleno}
                  styles={styleSelect}
                  theme={themeSelect}
                  classNamePrefix="tc-select"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-bold">Folio desde</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Ej: 20000"
                  value={folioMin}
                  onChange={(e) => {
                    setFolioMin(e.target.value);
                    setShowPdf(false);
                  }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-bold">Folio hasta</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Ej: 20500"
                  value={folioMax}
                  onChange={(e) => {
                    setFolioMax(e.target.value);
                    setShowPdf(false);
                  }}
                />
              </div>
              {ticketsFiltrados.length > 0 && !showPdf && (
                <div className="col-md-auto align-self-end mb-1 mt-3">
                  <button className="btn btn-sm btn-primary" onClick={() => setShowPdf(true)}>
                    <i className="bi bi-printer me-2"></i>
                    Imprimir ({ticketsFiltrados.length})
                  </button>
                </div>
              )}
            </div>

            {/* Vista PDF */}
            {showPdf && ticketsFiltrados.length > 0 && (
              <div className="mb-6">
                <TicketsPdf tickets={ticketsFiltrados} handleClose={() => setShowPdf(false)} />
              </div>
            )}

            {/* Grid de tickets */}
            {!showPdf && (
              <>
                {loadingTickets && (
                  <div className="d-flex justify-content-center py-10">
                    <span className="indicator-progress" style={{ display: 'block' }}>
                      Cargando tickets...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </div>
                )}
                {!loadingTickets && eventoSeleccionado && tickets.length === 0 && (
                  <div className="text-center text-muted py-10">
                    No hay tickets activos para este evento.
                  </div>
                )}
                {!loadingTickets && tickets.length > 0 && ticketsFiltrados.length === 0 && (
                  <div className="text-center text-muted py-10">
                    No hay tickets en el rango de folios indicado.
                  </div>
                )}
                {!loadingTickets && ticketsFiltrados.length > 0 && (
                  <>
                    <div className="text-muted mb-3 fs-7">
                      {ticketsFiltrados.length} ticket(s) encontrado(s)
                      {(folioMin || folioMax) && (
                        <span className="ms-2 text-primary fw-bold">
                          (folios {folioMin || '—'} a {folioMax || '—'})
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {ticketsFiltrados.map((ticket: any) => (
                        <TicketCard key={ticket.id} ticket={ticket} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Columna derecha: Insertar en BD ─────────────────────────────── */}
      <div className="col-lg-4">
        <InsertPanel eventoActivo={eventoActivo} eventoActivoId={eventoActivoId} onInsertSuccess={() => {}} />
      </div>
    </div>
  );
};

export { View };
